import type { World } from './world.js';
import type { EngineConfig, EnemyDef, SpawnEvent } from '../types.js';

/**
 * Walks level.spawns sorted by `t`. Each tick advances timeMs and spawns any
 * events whose `t*1000 <= timeMs`. After durationSec, triggers boss spawn once.
 */
export class SpawnDirector {
  private cursor = 0;
  private sorted: SpawnEvent[] = [];
  private bossSpawned = false;

  constructor(private config: EngineConfig) {
    this.sorted = [...config.level.spawns].sort((a, b) => a.t - b.t);
  }

  reset(): void {
    this.cursor = 0;
    this.bossSpawned = false;
  }

  update(world: World, _dtMs: number): void {
    // Drain any events whose time has arrived.
    while (this.cursor < this.sorted.length) {
      const ev = this.sorted[this.cursor];
      if (ev.t * 1000 > world.timeMs) break;
      this.dispatch(world, ev);
      this.cursor++;
    }

    // Boss spawn at level end.
    if (!this.bossSpawned && world.timeMs >= this.config.level.durationSec * 1000) {
      const bossDef = this.config.bosses[this.config.level.bossId];
      if (bossDef) {
        world.spawnBoss({
          defId: bossDef.id,
          pos: { x: this.config.worldWidth * 0.5, y: 120 },
          radius: bossDef.radius,
          hp: bossDef.hp,
        });
      }
      this.bossSpawned = true;
    }
  }

  get bossHasSpawned(): boolean {
    return this.bossSpawned;
  }

  private dispatch(world: World, ev: SpawnEvent): void {
    const def = this.config.enemies[ev.enemyId];
    if (!def) return;

    const count = Math.max(1, ev.count ?? 1);
    const spacing = ev.spacing ?? 30;
    const group = ev.group ?? '';

    for (let i = 0; i < count; i++) {
      // formation: horizontal fan centered on ev.x
      const offset = count > 1 ? (i - (count - 1) / 2) * spacing : 0;
      const spawnX = ev.x + offset;
      this.spawnOne(world, def, spawnX, group, i);
    }
  }

  private spawnOne(
    world: World,
    def: EnemyDef,
    x: number,
    group: string,
    swarmIndex: number
  ): void {
    const vel = { x: 0, y: def.speed };
    // Aimed enemies move slower vertically.
    if (def.archetype === 'aimed') {
      vel.y = def.speed;
    }

    world.spawnEnemy({
      defId: def.id,
      archetype: def.archetype,
      pos: { x, y: -def.radius },
      vel,
      radius: def.radius,
      hp: def.hp,
      scoreValue: def.scoreValue,
      fireRateHz: def.fireRateHz ?? 0,
      bulletSpeed: def.bulletSpeed ?? 0,
      amplitude: def.params?.amplitude ?? 0,
      frequency: def.params?.frequency ?? 0,
      group,
      swarmIndex,
    });
  }
}
